import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { LayoutContext } from "../../context";
import { Button, ButtonGroup, Box, Chip, Grid, Input, LinearProgress, Select, Option, Sheet, Stack, Typography } from "@mui/joy";
import FilterListRoundedIcon from '@mui/icons-material/FilterListRounded';
import SortRoundedIcon from '@mui/icons-material/SortRounded';
import CalendarMonthRoundedIcon from '@mui/icons-material/CalendarMonthRounded';

import listPlugin from '@fullcalendar/list';
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from '@fullcalendar/daygrid'; // Подключение вида "месяц"
import timeGridPlugin from '@fullcalendar/timegrid'; // Подключение вида "неделя/день"
import interactionPlugin from '@fullcalendar/interaction'; // Для обработки событий клика
import { useMediaQuery, useTheme } from "@mui/material";
import { APIGetEvents } from "../../api";
import { useVisiblePolling } from '../../hooks/useVisiblePolling';
import { reconcileEvents } from '../../utils/reconcileEvents';


// Типы представлений
const VIEW_TYPES = {
  MONTH: 'dayGridMonth',
  WEEK: 'timeGridWeek', 
  DAY: 'timeGridDay',
  LIST: 'listWeek'
};






export default function CalendarBlock({ viewMode = 'calendar' }) {
    const { listEvents, setLoadListEvents, setListEvents, setModalEvent, dateRange, setCurrentDateRange, updateListEvents, tokenEmployee } = useContext(LayoutContext);
    const [ resources, setResources ] = useState([]);
    const [ events, setEvents ] = useState([]);

    const theme = useTheme();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
    // Состояние для текущего представления
    const [currentView, setCurrentView] = useState(VIEW_TYPES.MONTH);

    const loadedMonthsRef = useRef(new Set());
    const inFlightMonthsRef = useRef(new Set()); // месяцы, по которым запрос УЖЕ летит
    const calendarRef = useRef();
    const [isLoading, setIsLoading] = useState(false);

    // ── Фильтр и сортировка для режимов список/карточки ──
    const [stateFilter, setStateFilter] = useState('all');
    const [sortBy, setSortBy] = useState('date_event'); // 'date_event' | 'date_create' | 'drawing_deadline' | 'completed_first' | 'not_completed_first'

    // ── Диапазон дат для list/grid ──
    const now = new Date();
    const defaultStart = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0];
    const defaultEnd = new Date(now.getFullYear(), now.getMonth() + 1, 0).toISOString().split('T')[0];

    // Применённые даты (по ним фильтруются события)
    const [dateFrom, setDateFrom] = useState(defaultStart);
    const [dateTo, setDateTo] = useState(defaultEnd);

    // A loaded month is a cache, not a subscription to changes made by surveyors.
    const refreshStart = viewMode === 'calendar' ? dateRange.startDate : dateFrom;
    const refreshEnd = viewMode === 'calendar' ? dateRange.endDate : dateTo;
    const refreshScope = `${tokenEmployee}:${viewMode}:${refreshStart}:${refreshEnd}`;
    const refreshScopeRef = useRef(refreshScope);
    refreshScopeRef.current = refreshScope;
    useEffect(() => { refreshScopeRef.current = refreshScope; return () => { refreshScopeRef.current = null; }; }, [refreshScope]);
    useVisiblePolling(async () => {
        const scope = refreshScope;
        const data = await APIGetEvents(null, null, refreshStart, refreshEnd, true);
        if (refreshScopeRef.current !== scope || !Array.isArray(data)) return;
        setListEvents(previous => refreshScopeRef.current === scope
            ? reconcileEvents(previous, data, { replaceRange: true, start: refreshStart, end: refreshEnd })
            : previous);
    }, Boolean(tokenEmployee && refreshStart && refreshEnd));

    // Черновые даты (пока пользователь выбирает в инпутах)
    const [draftDateFrom, setDraftDateFrom] = useState(defaultStart);
    const [draftDateTo, setDraftDateTo] = useState(defaultEnd);

    // Загрузка среза
    const [sliceLoading, setSliceLoading] = useState(false);

    // Есть ли несохранённые изменения дат
    const datesChanged = draftDateFrom !== dateFrom || draftDateTo !== dateTo;

    // Применить срез — запрос на сервер + обновление фильтра
    const applyDateSlice = useCallback(() => {
        if (!draftDateFrom || !draftDateTo) return;

        setSliceLoading(true);
        setLoadListEvents(true);

        APIGetEvents(null, null, draftDateFrom, draftDateTo, true)
            .then((data) => {
                if (!Array.isArray(data)) throw new Error('Не удалось загрузить события');
                if (Array.isArray(data)) {
                    setListEvents(prevEvents => {
                        const filtered = prevEvents.filter(event => {
                            const d = new Date(event.date_event);
                            const s = new Date(draftDateFrom);
                            const e = new Date(draftDateTo);
                            return d < s || d > e;
                        });
                        return [...filtered, ...data];
                    });
                }
                // Применяем даты только после успешного ответа
                setDateFrom(draftDateFrom);
                setDateTo(draftDateTo);
            })
            .catch((err) => {
                console.error('Ошибка загрузки среза:', err);
            })
            .finally(() => {
                setSliceLoading(false);
                setLoadListEvents(false);
            });
    }, [draftDateFrom, draftDateTo, setListEvents, setLoadListEvents]);


    // Функция для смены представления через API FullCalendar
    const changeView = (viewType) => {
      const calendarApi = calendarRef.current?.getApi();
      if (calendarApi) {
        calendarApi.changeView(viewType);
        setCurrentView(viewType);
      }
    };

    function isDrawingOverdue(event) {
      if (event.state !== 'drawing') return false;
      if (!event.drawing_deadline) return false;

      // Было: new Date(new Date().toLocaleString('en-US', { timeZone: 'Europe/Moscow' })).
      // toLocaleString отдаёт строку без указания зоны, и new Date разбирает её
      // как ЛОКАЛЬНОЕ время браузера. У пользователя не из МСК «сейчас» уезжало
      // на разницу зон, и просрочка определялась неверно.
      const ts = Date.parse(event.drawing_deadline);
      if (!Number.isFinite(ts)) return false;
      return Date.now() > ts;
    }

    // Событие закрыто обмерщиком, но дедлайна нет — это сбой, его надо видеть
    function isDeadlineMissing(event) {
      if (!['drawing'].includes(event.state)) return false;
      return !event.drawing_deadline;
    }

    // Функция для получения диапазона дат месяца
    const getMonthRange = (date) => {
      const start = new Date(date.getFullYear(), date.getMonth(), 1);
      const end = new Date(date.getFullYear(), date.getMonth() + 1, 1);
      return { start, end };
    };

    // Обработчик изменения даты/вида календаря
    const handleDatesSet = (arg) => {
      const calendarApi = calendarRef?.current?.getApi();
      console.log(calendarApi);
      if (calendarApi) {
        const currentDate = calendarApi.getDate();
        const { start, end } = getMonthRange(currentDate);
        
        // Сохраняем диапазон в контекст
        setCurrentDateRange(
          start.toISOString().split('T')[0],
          end.toISOString().split('T')[0]
        );

        // Форматируем даты для запроса
        const startStr = start.toISOString().split('T')[0];
        const endStr = end.toISOString().split('T')[0];
        
        // Проверяем, загружали ли уже этот месяц
        const monthKey = `${start.getFullYear()}-${start.getMonth()}`;

        
        // Уже загружен или прямо сейчас грузится — выходим
        if (loadedMonthsRef.current.has(monthKey) || inFlightMonthsRef.current.has(monthKey)) {
            return;
        }

        // Помечаем как «в полёте» СИНХРОННО — следующие вызовы не пройдут
        inFlightMonthsRef.current.add(monthKey);

        setIsLoading(true);
        setLoadListEvents(true);

        APIGetEvents(null, null, startStr, endStr, true)
          .then((data) => {
              if (!Array.isArray(data)) throw new Error('Не удалось загрузить события');
              console.log("Events", data);
              
              if (Array.isArray(data)) {
                  setListEvents(prevEvents => {
                      if (startStr && endStr) {
                          const filtered = prevEvents.filter(event => {
                              const eventDate = new Date(event.date_event);
                              const s = new Date(startStr);
                              const e = new Date(endStr);
                              return eventDate < s || eventDate >= e;
                          });
                          return [...filtered, ...data];
                      }
                      const newEventsMap = new Map(data.map(event => [event.id, event]));
                      return [...prevEvents.filter(event => !newEventsMap.has(event.id)), ...data];
                  });
              }
              // Помечаем месяц как загруженный ТОЛЬКО после успешного ответа
              loadedMonthsRef.current.add(monthKey);
          })
          .catch((err) => {
              console.error('Ошибка загрузки событий:', err);
              // НЕ помечаем как загруженный — даём шанс повторить
          })
          .finally(() => {
              inFlightMonthsRef.current.delete(monthKey);
              setLoadListEvents(false);
              setIsLoading(false);
          });
      }
    };

    useEffect(() => {
      const t = setTimeout(() => handleDatesSet(), 0);
      return () => clearTimeout(t);
    }, []);
  
    // Обработка клика на дату
    const handleDateClick = (info) => {
      const today = new Date();
      const clickedDate = new Date(info.date);

      if (clickedDate < today.setHours(0, 0, 0, 0)) {
        alert("Нельзя создавать события на прошедшие даты.");
      } else {
        setModalEvent({ event: null, date: info?.dateStr, open: true });
      }
    };
  
    // Обработка клика на событие
    const handleEventClick = (info) => {
      setModalEvent({ event: listEvents.find((item) => item.id == info.event.id), open: true });
    };

    // Обработчик перемещения события
    const handleEventChange = (changeInfo) => {
      const updatedEvent = {
        id: changeInfo.event.id,
        start: changeInfo.event.start.toISOString(), // Преобразуем в ISO формат
        end: changeInfo.event.end ? changeInfo.event.end.toISOString() : null, // Если есть end
      };
    };
    
    // Обработчик изменения представления в календаре
    const handleViewDidMount = (viewInfo) => {
      setCurrentView(viewInfo.view.type);
    };

    // ── Конфиг состояний для карточек/списка ──
    const STATE_CONFIG = {
        measuring_premises:   { label: 'Обмер помещения', color: 'primary', accent: 'cornflowerblue', bgSoft: 'rgba(100, 149, 237, 0.08)' },
        drawing:              { label: 'Черчение',         color: 'warning', accent: '#f9f111',        bgSoft: 'rgba(249, 241, 17, 0.08)' },
        completed:            { label: 'Завершён',         color: 'success', accent: 'green',          bgSoft: 'rgba(0, 128, 0, 0.08)' },
        additional_measuring: { label: 'Доп обмер',        color: 'warning', accent: '#cd7b00',        bgSoft: 'rgba(205, 123, 0, 0.08)' },

        // Анкета
        cancelled:            { label: 'Отменён',          color: 'danger',  accent: '#cd1f1f',        bgSoft: 'rgba(205, 31, 31, 0.08)' },
        moved:                { label: 'Перенесён',        color: 'warning', accent: '#cd7b00',        bgSoft: 'rgba(205, 123, 0, 0.08)' },
        postponed_to_date:    { label: 'Перенесён на дату',color: 'warning', accent: '#cd7b00',        bgSoft: 'rgba(205, 123, 0, 0.08)' },
    };
    
    // На случай, если бэк ещё не отдаёт effective_state — собираем сами
    const resolveEffectiveState = (event) => {
        if (event.effective_state) return event.effective_state;

        const qStatus = event.questionnaire?.status;
        if (qStatus === 'cancelled' || qStatus === 'moved' || qStatus === 'postponed_to_date') {
            return qStatus;
        }
        if (event.is_additional_measuring) return 'additional_measuring';
        return event.state;
    };

    // ── Фильтрация + сортировка для списка/карточек ──
    const sortedEvents = useMemo(() => {
        let list = [...(listEvents || [])];

        // Фильтр по диапазону дат
        if (dateFrom) {
            list = list.filter(e => e.date_event >= dateFrom);
        }
        if (dateTo) {
            list = list.filter(e => e.date_event <= dateTo);
        }

        // Фильтр по стадии
        if (stateFilter !== 'all') {
            list = list.filter(e => resolveEffectiveState(e) === stateFilter);
        }

        // Сортировка
        list.sort((a, b) => {
            switch (sortBy) {
                case 'date_event':
                    return new Date(b.date_event) - new Date(a.date_event);
                case 'date_create':
                    return new Date(b.date_create || 0) - new Date(a.date_create || 0);
                case 'drawing_deadline': {
                    // События без дедлайна — в конец
                    const da = a.drawing_deadline ? new Date(a.drawing_deadline) : null;
                    const db = b.drawing_deadline ? new Date(b.drawing_deadline) : null;
                    if (!da && !db) return 0;
                    if (!da) return 1;
                    if (!db) return -1;
                    return da - db; // ближайший дедлайн сверху
                }
                case 'completed_first': {
                    const order = { completed: 0, cancelled: 1, moved: 2, postponed_to_date: 3, drawing: 4, additional_measuring: 5, measuring_premises: 6 };
                    return (order[resolveEffectiveState(a)] ?? 9) - (order[resolveEffectiveState(b)] ?? 9);
                }
                case 'not_completed_first': {
                    const order = { measuring_premises: 0, additional_measuring: 1, drawing: 2, moved: 3, postponed_to_date: 4, completed: 5, cancelled: 6 };
                    return (order[resolveEffectiveState(a)] ?? 9) - (order[resolveEffectiveState(b)] ?? 9);
                }
                default:
                    return 0;
            }
        });

        return list;
    }, [listEvents, stateFilter, sortBy, dateFrom, dateTo]);

    // ── Панель фильтров (для list и grid) ──
    const filterBar = (
        <Stack
            direction={{ xs: 'column', lg: 'row' }}
            spacing={1.5}
            sx={{ mb: 2 }}
        >
            <Select
                size="sm"
                value={stateFilter}
                onChange={(_, val) => setStateFilter(val)}
                startDecorator={<FilterListRoundedIcon sx={{ fontSize: 16 }} />}
                sx={{ minWidth: 180, borderRadius: 'lg', height: "fit-content" }}
            >
                <Option value="all">Все стадии</Option>
                <Option value="measuring_premises">Обмер помещения</Option>
                <Option value="drawing">Черчение</Option>
                <Option value="completed">Завершён</Option>
                <Option value="additional_measuring">Доп обмер</Option>
                <Option value="cancelled">Отменён</Option>
                <Option value="moved">Перенесён</Option>
                <Option value="postponed_to_date">Перенесён на дату</Option>
            </Select>

            <Select
                size="sm"
                value={sortBy}
                onChange={(_, val) => setSortBy(val)}
                startDecorator={<SortRoundedIcon sx={{ fontSize: 16 }} />}
                sx={{ minWidth: 200, borderRadius: 'lg', height: "fit-content" }}
            >
                <Option value="date_event">По дате события</Option>
                <Option value="date_create">По дате создания</Option>
                <Option value="drawing_deadline">По дедлайну</Option>
                <Option value="completed_first">Завершённые первые</Option>
                <Option value="not_completed_first">Незавершённые первые</Option>
            </Select>

            <Stack direction={"row"} alignItems="center" sx={{ gap: 1, flexWrap: 'wrap' }}>
              <Stack direction={"row"} alignItems="center" sx={{ gap: 1, flexWrap: 'wrap' }}>
                <Input
                    type="date"
                    size="sm"
                    value={draftDateFrom}
                    onChange={(e) => setDraftDateFrom(e.target.value)}
                    slotProps={{ input: { max: draftDateTo || undefined } }}
                    sx={{ minWidth: 140, borderRadius: 'lg' }}
                />
                <Typography level="body-xs" sx={{ color: 'text.tertiary', px: 0.25 }}>—</Typography>
                <Input
                    type="date"
                    size="sm"
                    value={draftDateTo}
                    onChange={(e) => setDraftDateTo(e.target.value)}
                    slotProps={{ input: { min: draftDateFrom || undefined } }}
                    sx={{ minWidth: 140, borderRadius: 'lg' }}
                />
                {datesChanged && (
                    <Button
                        size="sm"
                        variant="solid"
                        color="primary"
                        loading={sliceLoading}
                        onClick={applyDateSlice}
                        sx={{ borderRadius: 'lg', minWidth: 100 }}
                    >
                        Применить
                    </Button>
                )}
              </Stack>

              <Chip
                  size="sm"
                  variant="soft"
                  color="neutral"
                  sx={{ alignSelf: 'center' }}
              >
                  {sortedEvents.length} событий
              </Chip>

              {(stateFilter !== 'all' || dateFrom !== defaultStart || dateTo !== defaultEnd) && (
                  <Chip
                      size="sm"
                      variant="soft"
                      color="danger"
                      onClick={() => {
                          setStateFilter('all');
                          setDateFrom(defaultStart);
                          setDateTo(defaultEnd);
                          setDraftDateFrom(defaultStart);
                          setDraftDateTo(defaultEnd);
                      }}
                      sx={{ alignSelf: 'center', cursor: 'pointer' }}
                  >
                      Сбросить фильтры ✕
                  </Chip>
              )}
            </Stack>
        </Stack>
    );

    // ── Режим: Календарь ──
    if (viewMode === 'calendar') {
        return (
            <>
                {isLoading && <LinearProgress sx={{ mb: 1 }} />}
                <FullCalendar
                    ref={calendarRef}
                    plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin, listPlugin]}
                    initialView={currentView}
                    firstDay={1}
                    events={listEvents}
                    dateClick={handleDateClick}
                    eventClick={handleEventClick}
                    datesSet={handleDatesSet}
                    viewDidMount={handleViewDidMount}
                    eventChange={handleEventChange}
                    selectable={true}
                    headerToolbar={{
                        left: 'prev,next today',
                        center: 'title',
                        right: '',
                    }}
                    height="auto"
                    locale="ru"
                    timeZone="local"
                    fixedWeekCount={false}
                    showNonCurrentDates={false}
                    eventContent={(eventInfo) => {
                        const raw = listEvents.find(e => e.id == eventInfo.event.id);
                        const overdue = raw && isDrawingOverdue(raw);
                        const timeText = eventInfo.event.start
                            ? new Date(eventInfo.event.start).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })
                            : '';
                        return (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 4, overflow: 'hidden', width: '100%' }}>
                                <div style={{
                                    marginLeft: 3, width: 8, height: 8, borderRadius: '50%',
                                    backgroundColor: (() => {
                                        const raw = listEvents.find(e => e.id == eventInfo.event.id);
                                        if (raw) {
                                            const effState = resolveEffectiveState(raw);
                                            const stateColors = {
                                                measuring_premises: 'cornflowerblue',
                                                drawing: '#f9f111',
                                                completed: 'green',
                                                additional_measuring: '#cd7b00',
                                                cancelled: '#cd1f1f',
                                                moved: '#cd7b00',
                                                postponed_to_date: '#cd7b00',
                                            };
                                            return stateColors[effState] || '#3788d8';
                                        }
                                        return eventInfo.event.backgroundColor || eventInfo.event.color || '#3788d8';
                                    })(),
                                    flexShrink: 0,
                                }} />
                                {timeText && (
                                    <span style={{ fontSize: '0.8em', fontWeight: 700, color: overdue ? '#e53935' : '#555', flexShrink: 0 }}>
                                        {timeText}
                                    </span>
                                )}
                                <span style={{
                                    fontSize: '0.85em', overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis',
                                    color: overdue ? '#e53935' : 'inherit', fontWeight: overdue ? 800 : 'normal',
                                }}>
                                    {`№${raw?.bitrix_deal?.id ?? ''} — ${raw?.bitrix_deal?.full_address || eventInfo.event.title}`}
                                </span>
                            </div>
                        );
                    }}
                />
            </>
        );
    }

    // ── Режим: Список ──
    if (viewMode === 'list') {
        return (
            <>
                {isLoading && <LinearProgress sx={{ mb: 1 }} />}
                {filterBar}
                {sortedEvents.length === 0 && !isLoading && (
                    <Stack alignItems="center" spacing={1} sx={{ py: 6, color: 'text.tertiary' }}>
                        <CalendarMonthRoundedIcon sx={{ fontSize: 48, opacity: 0.4 }} />
                        <Typography level="title-md">Событий не найдено</Typography>
                    </Stack>
                )}
                <Stack spacing={0.75}>
                    {sortedEvents.map((event) => {
                        const cfg = STATE_CONFIG[resolveEffectiveState(event)] || STATE_CONFIG.measuring_premises;
                        const overdue = isDrawingOverdue(event);
                        const timeStr = event.time_event
                            ? event.time_event.slice(0, 5)
                            : event.time_from && event.time_to
                                ? `${event.time_from.slice(0, 5)} – ${event.time_to.slice(0, 5)}`
                                : '';
                        return (
                            <Sheet
                                key={event.id}
                                onClick={() => setModalEvent({ event, open: true })}
                                sx={{
                                    borderRadius: 'lg', border: '1px solid', borderColor: 'divider',
                                    overflow: 'hidden', cursor: 'pointer', position: 'relative',
                                    transition: 'all 0.18s ease',
                                    '&:hover': { borderColor: cfg.accent, boxShadow: `0 2px 10px ${cfg.bgSoft}` },
                                }}
                            >
                                <Box sx={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: 3, bgcolor: event.color || cfg.accent }} />
                                <Stack direction="row" alignItems="center" sx={{ pl: 2.5, pr: 1.5, py: 1.25, gap: 1.5, flexWrap: 'wrap' }}>
                                    {/* Дата */}
                                    <Typography level="body-xs" sx={{ fontWeight: 700, color: overdue ? 'danger.500' : 'text.secondary', minWidth: 75 }}>
                                        {new Date(event.date_event).toLocaleDateString('ru-RU', { day: 'numeric', month: 'short', year: 'numeric' })}
                                    </Typography>
                                    {/* Время */}
                                    {timeStr && (
                                        <Typography level="body-xs" sx={{ fontWeight: 600, color: 'text.tertiary', minWidth: 50 }}>
                                            {timeStr}
                                        </Typography>
                                    )}
                                    {/* Статус */}
                                    <Chip size="sm" color={cfg.color} variant="soft" sx={{ fontSize: '0.68rem', minHeight: 20 }}>
                                        {cfg.label}
                                    </Chip>
                                    {event?.is_rework && (
                                        <Chip size="sm" color="danger" variant="solid" sx={{ fontSize: '0.68rem', minHeight: 20 }}>
                                            Не принято
                                        </Chip>
                                    )}
                                    {/* Название */}
                                    <Typography level="title-sm" sx={{
                                        fontWeight: 600, flex: 1, minWidth: 140,
                                        overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis',
                                        color: overdue ? '#e53935' : 'text.primary',
                                    }}>
                                        {event.title || `Событие #${event.id}`}
                                    </Typography>
                                    {/* Исполнитель */}
                                    {event.employee && (
                                        <Typography level="body-sm" sx={{ color: 'text.tertiary', minWidth: 100 }}>
                                            {event.employee.name} {event.employee.last_name || ''}
                                        </Typography>
                                    )}
                                </Stack>
                            </Sheet>
                        );
                    })}
                </Stack>
            </>
        );
    }

    // ── Режим: Карточки ──
    return (
        <>
            {isLoading && <LinearProgress sx={{ mb: 1 }} />}
            {filterBar}
            {sortedEvents.length === 0 && !isLoading && (
                <Stack alignItems="center" spacing={1} sx={{ py: 6, color: 'text.tertiary' }}>
                    <CalendarMonthRoundedIcon sx={{ fontSize: 48, opacity: 0.4 }} />
                    <Typography level="title-md">Событий не найдено</Typography>
                </Stack>
            )}
            <Grid container spacing={2} sx={{ alignItems: 'start' }}>
                {sortedEvents.map((event) => {
                    const cfg = STATE_CONFIG[resolveEffectiveState(event)] || STATE_CONFIG.measuring_premises;
                    const overdue = isDrawingOverdue(event);
                    const timeStr = event.time_event
                        ? event.time_event.slice(0, 5)
                        : event.time_from && event.time_to
                            ? `${event.time_from.slice(0, 5)} – ${event.time_to.slice(0, 5)}`
                            : '';
                    return (
                        <Grid key={event.id} xs={12} sm={6} lg={4} xl={3}>
                            <Sheet
                                onClick={() => setModalEvent({ event, open: true })}
                                sx={{
                                    borderRadius: 'xl', border: '1px solid', borderColor: 'divider',
                                    overflow: 'hidden', cursor: 'pointer',
                                    transition: 'all 0.2s ease',
                                    '&:hover': { borderColor: cfg.accent, boxShadow: `0 4px 16px ${cfg.bgSoft}`, transform: 'translateY(-2px)' },
                                }}
                            >
                                {/* Цветная полоска сверху */}
                                <Box sx={{ height: 4, bgcolor: event.color || cfg.accent }} />

                                <Box sx={{ p: 2 }}>
                                    {/* Шапка: дата + статус */}
                                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
                                        <Typography level="body-xs" sx={{ fontWeight: 700, color: overdue ? 'danger.500' : 'text.secondary' }}>
                                            {new Date(event.date_event).toLocaleDateString('ru-RU', { day: 'numeric', month: 'short', year: 'numeric' })}
                                            {timeStr && ` · ${timeStr}`}
                                        </Typography>
                                        <Chip size="sm" color={cfg.color} variant="soft" sx={{ fontSize: '0.68rem' }}>
                                            {cfg.label}
                                        </Chip>
                                    </Stack>

                                    {/* Заголовок */}
                                    <Typography level="title-sm" sx={{
                                        fontWeight: 700, mb: 1,
                                        overflow: 'hidden', display: '-webkit-box',
                                        WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
                                        color: overdue ? '#e53935' : 'text.primary',
                                    }}>
                                        {event.title || `Событие #${event.id}`}
                                    </Typography>

                                    {/* Сделка */}
                                    {/* {event.bitrix_deal && (
                                        <Typography level="body-xs" sx={{ color: 'text.tertiary', mb: 0.5 }}>
                                            Сделка #{event.bitrix_deal.id}
                                            {event.bitrix_deal.address && ` · ${event.bitrix_deal.address}`}
                                        </Typography>
                                    )} */}

                                    {/* Исполнитель */}
                                    {event.employee && (
                                        <Chip size="sm" variant="outlined" color="neutral" sx={{ mt: 0.5, fontSize: '0.7rem' }}>
                                            {event.employee.name} {event.employee.last_name || ''}
                                        </Chip>
                                    )}

                                    {/* Чертёжник */}
                                    {event.draftsman && (
                                        <Chip size="sm" variant="soft" color="warning" sx={{ mt: 0.5, ml: 0.5, fontSize: '0.7rem' }}>
                                            ✏️ {event.draftsman.name} {event.draftsman.last_name || ''}
                                        </Chip>
                                    )}
                                </Box>
                            </Sheet>
                        </Grid>
                    );
                })}
            </Grid>
        </>
    );
  };